import { useState, useEffect, useCallback } from 'react';
import { X, Copy, Download, Play, Check } from 'lucide-react';
import exploreApi from '../exploreApi';

export default function SubdomainSQLEditor({ isOpen, onClose, subdomain, sourceId }) {
  const [sqlStatements, setSqlStatements] = useState([]);
  const [editedSQL, setEditedSQL] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);

  const generateSQL = useCallback(async () => {
    if (!subdomain) return;
    setIsGenerating(true);
    setValidationErrors([]);
    try {
      const tableSchemas = subdomain.objects?.map(obj => ({
        path: obj.path,
        name: obj.name,
        type: obj.type || 'table',
        key_columns: obj.key_columns || [],
        row_count: obj.row_count,
      })) || [];

      const data = await exploreApi.generateSubdomainSQL(sourceId, {
        subdomain_name: subdomain.name,
        tables: subdomain.tables,
        relationships: subdomain.relationships || [],
        table_schemas: tableSchemas,
        subdomain_description: subdomain.description,
      });

      setSqlStatements(data.sql_statements || []);
      setEditedSQL((data.sql_statements || []).join('\n\n'));
    } catch (error) {
      setValidationErrors(['Failed to generate SQL statements']);
    } finally {
      setIsGenerating(false);
    }
  }, [subdomain, sourceId]);

  useEffect(() => {
    if (isOpen && subdomain && sqlStatements.length === 0) {
      generateSQL();
    }
  }, [isOpen, subdomain, sqlStatements.length, generateSQL]);

  if (!isOpen || !subdomain) return null;

  const validateSQL = async () => {
    setIsLoading(true);
    setValidationErrors([]);
    try {
      const result = await exploreApi.validateSQL(sourceId, editedSQL);
      if (!result.valid) {
        setValidationErrors(result.errors || ['Invalid SQL syntax']);
      } else {
        setValidationErrors([]);
        alert('SQL is valid!');
      }
    } catch {
      setValidationErrors(['Failed to validate SQL']);
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async (sql, index) => {
    try {
      await navigator.clipboard.writeText(sql);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch { /* silent */ }
  };

  const downloadSQL = () => {
    const blob = new Blob([editedSQL], { type: 'text/sql' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${subdomain.name.toLowerCase().replace(/\s+/g, '_')}_queries.sql`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">SQL Editor: {subdomain.name}</h2>
            <p className="text-sm text-gray-600 mt-1">
              {subdomain.tables.length} tables • Edit and validate SQL queries
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Tables in this subdomain:</h3>
            <div className="flex flex-wrap gap-2">
              {subdomain.tables.map((table, i) => (
                <span key={i} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">{table}</span>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700">Generated SQL Queries:</h3>
              <button
                onClick={generateSQL}
                disabled={isGenerating}
                className="px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primaryDark disabled:bg-gray-400 text-sm flex items-center gap-2"
              >
                <Play className="h-4 w-4" />
                {isGenerating ? 'Generating...' : 'Regenerate'}
              </button>
            </div>

            {isGenerating && (
              <div className="flex items-center justify-center py-8 text-gray-500">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-3" />
                Generating SQL...
              </div>
            )}

            {!isGenerating && (
              <div className="relative">
                <textarea
                  value={editedSQL}
                  onChange={e => setEditedSQL(e.target.value)}
                  className="w-full h-96 p-4 border rounded-lg font-mono text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="SQL queries will appear here..."
                  spellCheck={false}
                />
              </div>
            )}

            {validationErrors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-red-800 mb-2">Validation Errors:</h4>
                <ul className="list-disc list-inside space-y-1">
                  {validationErrors.map((err, i) => (
                    <li key={i} className="text-sm text-red-700">{err}</li>
                  ))}
                </ul>
              </div>
            )}

            {sqlStatements.length > 0 && (
              <div className="space-y-3 mt-6">
                <h3 className="text-sm font-semibold text-gray-700">Individual Statements:</h3>
                {sqlStatements.map((sql, i) => (
                  <div key={i} className="bg-gray-50 border rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-xs font-semibold text-gray-600">Query {i + 1}</h4>
                      <button
                        onClick={() => copyToClipboard(sql, i)}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        title="Copy to clipboard"
                      >
                        {copiedIndex === i ? (
                          <Check className="h-4 w-4 text-green-600" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    <pre className="text-xs font-mono text-gray-800 whitespace-pre-wrap">{sql}</pre>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <button
            onClick={validateSQL}
            disabled={isLoading || !editedSQL}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 text-sm"
          >
            {isLoading ? 'Validating...' : 'Validate SQL'}
          </button>
          <div className="flex gap-3">
            <button
              onClick={downloadSQL}
              disabled={!editedSQL}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400 text-sm flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Download
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primaryDark text-sm"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
